<template>
  <div
    v-if="meta && meta.totalPages > 1"
    class="w-full flex justify-center items-center mt-8 py-2"
  >
    <nav class="flex flex-wrap justify-center items-center space-x-2">
      <!-- Previous page button -->
      <button
        @click="handlePageChange(currentPage - 1)"
        :disabled="currentPage === 1"
        :class="[
          'px-4 py-2 rounded-md transition-colors duration-200',
          currentPage === 1
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
            : 'bg-gray-800 text-gray-300 hover:bg-gray-700',
        ]"
        :aria-label="'Prethodna stranica'"
      >
        &laquo;
      </button>

      <!-- Page numbers -->
      <template v-for="page in paginationRange" :key="page">
        <button
          v-if="page !== '...'"
          @click="typeof page === 'number' ? handlePageChange(page) : null"
          :class="[
            'px-4 py-2 rounded-md transition-colors duration-200',
            currentPage === page
              ? 'bg-green-600 text-white font-semibold'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700',
          ]"
          :aria-label="`Stranica ${page}`"
          :aria-current="currentPage === page ? 'page' : undefined"
        >
          {{ page }}
        </button>
        <span v-else class="px-2 py-2 text-gray-500 flex items-center">
          ...
        </span>
      </template>

      <!-- Next page button -->
      <button
        @click="handlePageChange(currentPage + 1)"
        :disabled="currentPage === meta.totalPages"
        :class="[
          'px-4 py-2 rounded-md transition-colors duration-200',
          currentPage === meta.totalPages
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
            : 'bg-gray-800 text-gray-300 hover:bg-gray-700',
        ]"
        :aria-label="'Sledeća stranica'"
      >
        &raquo;
      </button>
    </nav>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';

interface Meta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export default defineComponent({
  name: 'PaginationComponent',
  props: {
    meta: {
      type: Object as PropType<Meta | null>,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    maxVisiblePages: {
      type: Number,
      default: 7,
    },
    scrollToTop: {
      type: Boolean,
      default: true,
    },
    // Novo: kontroliše da li komponenta treba da ažurira URL
    updateUrl: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['page-change'],
  computed: {
    paginationRange(): (number | string)[] {
      if (!this.meta) return [];

      const totalPages = this.meta.totalPages;
      const currentPage = this.currentPage;
      const maxVisible = this.maxVisiblePages;

      if (totalPages <= maxVisible) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      let range: (number | string)[] = [];

      // Always show first page
      range.push(1);

      // Show ellipsis if current page is far from start
      if (currentPage > 3) {
        range.push('...');
      }

      // Calculate start and end of middle section
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        if (!range.includes(i)) {
          range.push(i);
        }
      }

      // Show ellipsis if current page is far from end
      if (currentPage < totalPages - 2) {
        range.push('...');
      }

      // Always show last page (if more than 1 page total)
      if (totalPages > 1 && !range.includes(totalPages)) {
        range.push(totalPages);
      }

      return range;
    },
  },
  methods: {
    handlePageChange(page: number | string) {
      const numPage =
        typeof page === 'number' ? page : parseInt(page as string, 10);

      if (
        !isNaN(numPage) &&
        numPage >= 1 &&
        this.meta &&
        numPage <= this.meta.totalPages &&
        numPage !== this.currentPage
      ) {
        if (this.updateUrl) {
          // Ažuriraj URL umesto emitovanja event-a
          this.$router.push({
            path: this.$route.path,
            query: { ...this.$route.query, page: numPage.toString() },
          });
        } else {
          // Standardno ponašanje - emit event
          this.$emit('page-change', numPage);
        }

        if (this.scrollToTop) {
          this.$nextTick(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          });
        }
      }
    },
  },
});
</script>
